import { type $Fetch, type Fetch } from 'ofetch'
import { ApiError } from '@/utils/error'
import { usePlatformStore } from '@/store-pinia/platform'
import { PlatformSource } from '@/view-models/platform'

interface FetchExtra extends Partial<Fetch> {
  $request: (url: string, config?: any) => Promise<any>
  $delete: (url: string, config?: any) => Promise<any>
  $get: (url: string, config?: any) => Promise<any>
  $head: (url: string, config?: any) => Promise<any>
  $options: (url: string, config?: any) => Promise<any>
  $post: (url: string, config?: any) => Promise<any>
  $put: (url: string, config?: any) => Promise<any>
  $patch: (url: string, config?: any) => Promise<any>
}

// Fetch.prototype cannot be modified
const fetchExtraProperties: Partial<FetchExtra> = {}

// Request helpers ($get, $post, ...)
for (const method of ['request', 'delete', 'get', 'head', 'options', 'post', 'put', 'patch']) {
  // @ts-ignore
  fetchExtraProperties['$' + method] = function (...args: any) {
    const [url, params = {}, config = {}] = args

    return method === 'get'
      ? // @ts-ignore
        this.apply(this, [url, { ...config, ...params }])
      : // @ts-ignore
        this.apply(this, [url, { ...config, body: params, method: method.toUpperCase() }])
  }
}

const extendInstance = (fetcher: any) => {
  for (const key in fetchExtraProperties) {
    // @ts-ignore
    fetcher[key] = fetchExtraProperties[key].bind(fetcher)
  }
}
const RETRY_COUNT = 3
const RETRY_DELAY_MS = 1000
const RETRY_STATUS_CODES = [408, 425, 500, 502, 503, 504]
const DEFAULT_LANG = 'en-US'

function createFetcher(options: any) {
  let nuxtApp = useNuxtApp()
  function updateNuxtAppInstanceIfNeeded() {
    const _nuxtApp = tryUseNuxtApp()

    if (import.meta.server && _nuxtApp) {
      nuxtApp = _nuxtApp
    }
  }
  return $fetch.create({
    baseURL: options.baseURL,
    credentials: 'include',
    retry: RETRY_COUNT,
    retryStatusCodes: RETRY_STATUS_CODES,
    retryDelay: context => {
      const currentRetryCount = RETRY_COUNT - (context.options.retry || 0) + 1

      return currentRetryCount * RETRY_DELAY_MS
    },
    onRequest(params) {
      const { options, request } = params
      updateNuxtAppInstanceIfNeeded()

      const { localeProperties } = nuxtApp.$i18n
      const acceptLang = localeProperties.value.name ?? DEFAULT_LANG
      options.headers = new Headers(options.headers)
      options.headers.delete('common') // ofetch bug workaround
      options.headers.set('Accept-Language', acceptLang)
      if (import.meta.client) {
        options.headers.set('Accept-Timezone', getTimeZone())
      } else {
        options.headers.set('Server-Side-Request', 'true')
      }

      // adapt funbook context
      const platformStore = usePlatformStore(nuxtApp.$pinia)
      const method = options.method?.toUpperCase() || 'GET'

      // ! workaround, for FunBook 1.0 branch API `from` qs
      const isFunBookV1BranchAPI = platformStore.isFunBookV1Path && method === 'GET' && /\/pub\/branch\/\d+$/.test(params.request as string)

      if (method === 'GET' && (platformStore.isFunBookV2 || isFunBookV1BranchAPI)) {
        options.query = { ...options.query, from: PlatformSource.FunBook }
      }

      options.headers.set('Platform', platformStore.name)
    },
    // onRequestError({ request, options, error }) {},
    // onResponse({ request, options, response }) {},
    onResponseError(context) {
      // const isErrorSkipped = RETRY_STATUS_CODES.includes(context.response.status) && context.options.retry !== 0
      // if (isErrorSkipped) {
      //   return
      // }
      //  TODO: error handler with error message
      // if (context.response?._data?.message) {
      //   useAlertStore().error(context.response._data.message)
      // }
      console.error('onResponseError', context.error?.message || context.response._data.message, context.request)
      throw new ApiError(context.response._data.message, context.response.status, context.response._data.code)
    },
    ...options,
  })
}

let apiClient: ReturnType<typeof createFetcher>

export function useApiClient() {
  if (apiClient) return apiClient
  // runtimeConfig
  const config = useRuntimeConfig()
  const reqUrl = useRequestURL()
  const defaultBaseUrl = `${reqUrl.protocol}//${reqUrl.hostname}/v2/funnow`

  // baseURL
  const baseURL = config.public.CONFIG_ENV === 'local' ? '/api/proxy' : defaultBaseUrl

  const fetchOptions = {
    baseURL,
    headers: {
      common: {},
    } as any,
  }

  if (import.meta.server) {
    // Don't accept brotli encoding because Node can't parse it
    fetchOptions.headers.common['accept-encoding'] = 'gzip, deflate'
  }

  apiClient = createFetcher(fetchOptions)
  extendInstance(apiClient)

  return apiClient
}
